import React from 'react';
import { Box, Text } from '@gluestack-ui/themed';

/**
 * Color mappings for different state values.
 *
 * Design principle: Only signal states that need attention.
 * - Expected/default states (active, published) = neutral gray (no signal)
 * - Warning states (stale, pending) = amber/yellow (needs attention)
 * - Error states (rejected, failed) = red (problem)
 * - Completed states (finished) = subtle purple (done)
 */
const STATE_COLORS = {
  // Expected/default states - neutral gray (these are "normal", no signal needed)
  active: { bg: '#f3f4f6', text: '#4b5563', border: '#e5e7eb' },
  published: { bg: '#f3f4f6', text: '#4b5563', border: '#e5e7eb' },
  approved: { bg: '#f3f4f6', text: '#4b5563', border: '#e5e7eb' },

  // In-flight states - info blue (autograde / processing, not an error)
  processing: { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },

  // Warning states - amber/yellow (needs attention)
  pending: { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
  pending_approval: { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
  stale: { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },

  // Neutral/inactive states - lighter gray
  inactive: { bg: '#f9fafb', text: '#9ca3af', border: '#e5e7eb' },
  draft: { bg: '#f9fafb', text: '#6b7280', border: '#e5e7eb' },
  retired: { bg: '#f9fafb', text: '#9ca3af', border: '#e5e7eb' },

  // Completed state - subtle purple
  finished: { bg: '#f5f3ff', text: '#6d28d9', border: '#ddd6fe' },

  // Error states - red (problem)
  rejected: { bg: '#fef2f2', text: '#b91c1c', border: '#fecaca' },
  failed: { bg: '#fef2f2', text: '#b91c1c', border: '#fecaca' },

  // Default fallback - gray
  default: { bg: '#f3f4f6', text: '#6b7280', border: '#e5e7eb' },
};

/**
 * Get color config for a state value, with fuzzy matching
 */
const getStateColors = (state) => {
  if (!state) return STATE_COLORS.default;

  const normalizedState = String(state).toLowerCase().trim();

  // Direct match
  if (STATE_COLORS[normalizedState]) {
    return STATE_COLORS[normalizedState];
  }

  // Fuzzy matching for common patterns
  if (normalizedState.includes('active')) return STATE_COLORS.active;
  if (normalizedState.includes('publish')) return STATE_COLORS.published;
  if (normalizedState.includes('approve')) return STATE_COLORS.approved;
  if (normalizedState.includes('process')) return STATE_COLORS.processing;
  if (normalizedState.includes('pending')) return STATE_COLORS.pending;
  if (normalizedState.includes('stale')) return STATE_COLORS.stale;
  if (normalizedState.includes('draft')) return STATE_COLORS.draft;
  if (normalizedState.includes('inactive')) return STATE_COLORS.inactive;
  if (normalizedState.includes('retire')) return STATE_COLORS.retired;
  if (normalizedState.includes('finish')) return STATE_COLORS.finished;
  if (normalizedState.includes('reject')) return STATE_COLORS.rejected;
  if (normalizedState.includes('fail')) return STATE_COLORS.failed;

  return STATE_COLORS.default;
};

/**
 * Format state value for display (capitalize, replace underscores)
 */
const formatStateLabel = (state) => {
  if (!state) return '';
  return String(state)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * StatusBadge - A compact, colored badge for displaying state/status values.
 * Automatically maps state values to appropriate colors.
 */
export const StatusBadge = ({ value, size = 'sm' }) => {
  if (!value) return null;

  const colors = getStateColors(value);
  const label = formatStateLabel(value);

  const sizeStyles = {
    xs: { px: 6, py: 1, fontSize: 10 },
    sm: { px: 8, py: 2, fontSize: 11 },
    md: { px: 10, py: 3, fontSize: 12 },
  };

  const styles = sizeStyles[size] || sizeStyles.sm;

  return (
    <Box
      style={{
        backgroundColor: colors.bg,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 4,
        paddingHorizontal: styles.px,
        paddingVertical: styles.py,
        alignSelf: 'flex-start',
      }}
    >
      <Text
        style={{
          color: colors.text,
          fontSize: styles.fontSize,
          fontWeight: '500',
          letterSpacing: 0,
        }}
      >
        {label}
      </Text>
    </Box>
  );
};

export default StatusBadge;
